import type { Layer, Line, Point, Shape } from '../../../context/context';
import { liftangoBrandTokens } from '../../../theme';

export const LEGACY_PICKUP_COLOR = '#FF0000';
export const LEGACY_DROPOFF_COLOR = '#00FF00';
export const LEGACY_TRIP_LINE_COLOR = '#0000FF';

export type TripLineShape = Line & (
  { ride_id: string } |
  { originPointId: string; destPointId: string }
);

export const isTripLineShape = (shape: Shape): shape is TripLineShape =>
  shape.type === 'line' && (
    Boolean((shape as any).ride_id) ||
    (
      'originPointId' in shape &&
      'destPointId' in shape &&
      Boolean((shape as any).originPointId) &&
      Boolean((shape as any).destPointId)
    )
  );

export const collectTripPointIds = (shapes: Shape[]): Set<string> => {
  const ids = new Set<string>();
  shapes.forEach((shape) => {
    if (!isTripLineShape(shape)) return;
    if ((shape as any).originPointId) {
      ids.add((shape as any).originPointId);
    }
    if ((shape as any).destPointId) {
      ids.add((shape as any).destPointId);
    }
  });
  return ids;
};

const resolveLegacyPointColor = (color: string): string | null => {
  if (color === LEGACY_PICKUP_COLOR) return liftangoBrandTokens.missionControl.tripPickup;
  if (color === LEGACY_DROPOFF_COLOR) return liftangoBrandTokens.missionControl.tripDropoff;
  return null;
};

export const getResolvedMissionControlPoint = (
  point: Point,
  isTripPointLinked: boolean,
  allowColorFallback: boolean
): { color: string; icon: Point['icon']; size: number; remapped: boolean } => {
  const mappedColor = resolveLegacyPointColor(point.colour);
  const shouldRemap =
    mappedColor !== null && (
      isTripPointLinked ||
      (allowColorFallback && point.icon === 'location')
    );

  if (!shouldRemap) {
    return {
      color: point.colour,
      icon: point.icon || 'default',
      size: point.size ?? 24,
      remapped: false,
    };
  }

  return {
    color: mappedColor,
    icon: 'dot',
    size: 12,
    remapped: true,
  };
};

export const getResolvedMissionControlLine = (
  line: Line,
  isTripLine: boolean
): { borderColor: string; opacity: number; width: number; remapped: boolean } => {
  const isLegacyTripLine =
    isTripLine &&
    (line.borderColour === LEGACY_TRIP_LINE_COLOR || line.colour === LEGACY_TRIP_LINE_COLOR);

  if (!isLegacyTripLine) {
    return {
      borderColor: line.borderColour || '#3388ff',
      opacity: parseFloat(line.opacity),
      width: line.borderWidth || 2,
      remapped: false,
    };
  }

  return {
    borderColor: liftangoBrandTokens.missionControl.tripLine,
    opacity: 0.85,
    width: 1.5,
    remapped: true,
  };
};

export const migrateMissionControlTripStyles = (layers: Layer[]): { layers: Layer[]; changed: boolean } => {
  let changed = false;

  const migratedLayers = layers.map((layer) => {
    const tripPointIds = collectTripPointIds(layer.shapes);
    const hasLinkedTripPoints = tripPointIds.size > 0;

    const migratedShapes = layer.shapes.map((shape) => {
      if (shape.type === 'point') {
        const point = shape as Point;
        const resolved = getResolvedMissionControlPoint(point, tripPointIds.has(point.id), !hasLinkedTripPoints);
        if (!resolved.remapped) return shape;
        changed = true;
        return {
          ...point,
          colour: resolved.color,
          icon: resolved.icon,
          size: resolved.size,
        };
      }

      if (shape.type === 'line') {
        const line = shape as Line;
        const resolved = getResolvedMissionControlLine(line, isTripLineShape(shape));
        if (!resolved.remapped) return shape;
        changed = true;
        return {
          ...line,
          colour: resolved.borderColor,
          borderColour: resolved.borderColor,
          opacity: String(resolved.opacity),
          borderWidth: resolved.width,
        };
      }

      return shape;
    });

    if (migratedShapes === layer.shapes) return layer;
    return { ...layer, shapes: migratedShapes };
  });

  return { layers: migratedLayers, changed };
};

